import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button, Input, ConfirmDialog } from '../components/ui'
import { useUserStore } from '../store/useUserStore'
import { useExerciseStore } from '../store/useExerciseStore'
import { useYearReviewStore } from '../store/useYearReviewStore'
import { useIntentionStore } from '../store/useIntentionStore'
import { useWrappedStore } from '../store/useWrappedStore'
import { useKidsStore } from '../store/useKidsStore'
import { deleteJourney } from '../services/api'

export default function Welcome() {
  const navigate = useNavigate()
  const { name: savedName, userId, login, yearsWithData, isLoading, clear, currentYear } = useUserStore()
  const [inputName, setInputName] = useState(savedName || '')
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    type: 'startFresh' | 'resetAll' | null
    isOpen: boolean
  }>({ type: null, isOpen: false })
  const lastYear = currentYear - 1
  const { wrapped } = useWrappedStore()
  const hasGeneratedWrapped = !!wrapped.generatedAt

  const hasPastYearData = yearsWithData.includes(lastYear)
  const hasCurrentYearData = yearsWithData.includes(currentYear)

  // Prevent double-clicks by checking if already logging in
  const handleStart = async () => {
    if (isLoggingIn || !inputName.trim()) return

    setIsLoggingIn(true)
    setLoginError(null)

    try {
      await login(inputName.trim())
      // If they already have a wrapped, go straight there
      navigate(hasGeneratedWrapped ? '/summary' : '/hub')
    } catch (err) {
      console.error('Login failed:', err)
      setLoginError('Something went wrong. Please try again.')
      setIsLoggingIn(false)
    }
  }

  const handleContinue = () => {
    // If wrapped already generated, go to summary
    navigate(hasGeneratedWrapped ? '/summary' : '/hub')
  }

  const handleReview = async () => {
    if (isLoggingIn || !inputName.trim()) return

    setIsLoggingIn(true)
    setLoginError(null)

    try {
      await login(inputName.trim())
      navigate('/review')
    } catch (err) {
      console.error('Login failed:', err)
      setLoginError('Something went wrong. Please try again.')
      setIsLoggingIn(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoggingIn) {
      handleStart()
    }
  }

  const handleNotMe = () => {
    // Clear all Zustand stores
    clear()
    useExerciseStore.getState().reset()
    useYearReviewStore.getState().reset()
    useIntentionStore.getState().reset()
    useWrappedStore.getState().reset()
    useKidsStore.getState().reset()

    // Clear ALL localStorage to prevent any data leakage
    localStorage.clear()

    setInputName('')
    setLoginError(null)
  }

  const handleStartFresh = () => {
    setConfirmDialog({ type: 'startFresh', isOpen: true })
  }

  const handleResetAll = () => {
    setConfirmDialog({ type: 'resetAll', isOpen: true })
  }

  const handleConfirmAction = async () => {
    const actionType = confirmDialog.type
    setConfirmDialog({ type: null, isOpen: false })

    if (actionType === 'startFresh') {
      setIsResetting(true)

      // Delete from database if logged in
      if (userId) {
        await deleteJourney(userId, currentYear)
      }

      // Reset local stores
      useExerciseStore.getState().reset()
      useYearReviewStore.getState().reset()
      useIntentionStore.getState().reset()
      useWrappedStore.getState().reset()

      // Update yearsWithData to remove current year
      const newYearsWithData = yearsWithData.filter((y) => y !== currentYear)
      useUserStore.getState().setYearsWithData(newYearsWithData)

      setIsResetting(false)
      navigate('/hub')
    } else if (actionType === 'resetAll') {
      setIsResetting(true)

      // Delete from database if logged in
      if (userId) {
        await deleteJourney(userId) // No year = delete all
      }

      // Reset all local stores
      useUserStore.getState().clear()
      useExerciseStore.getState().reset()
      useYearReviewStore.getState().reset()
      useIntentionStore.getState().reset()
      useWrappedStore.getState().reset()
      useKidsStore.getState().reset()

      // Clear localStorage for good measure
      localStorage.clear()

      setIsResetting(false)
      setInputName('')
      window.location.reload()
    }
  }

  const handleCancelDialog = () => {
    setConfirmDialog({ type: null, isOpen: false })
  }

  const handleKidsMode = () => {
    useUserStore.getState().setKidsMode(true)
    navigate('/kids')
  }

  // Loading state
  if (isLoading || isLoggingIn || isResetting) {
    return (
      <div className="wrapped-card bg-gradient-to-br from-purple to-pink">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="text-6xl"
          >
            ✨
          </motion.div>
          <p className="text-white/80 text-xl font-medium">
            {isResetting ? 'Clearing data...' : isLoggingIn ? 'Getting things ready...' : 'Loading...'}
          </p>
        </motion.div>
      </div>
    )
  }

  // Returning user with current year progress
  if (savedName && hasCurrentYearData) {
    return (
      <div className="wrapped-card bg-gradient-to-br from-purple to-pink">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8 max-w-md"
        >
          {/* Welcome back badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring' }}
            className="mx-auto w-20 h-20 rounded-full bg-white/20 flex items-center justify-center"
          >
            <span className="text-4xl">👋</span>
          </motion.div>

          {/* Welcome message */}
          <div className="space-y-2 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="wrapped-title"
            >
              Welcome back, {savedName}!
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white/80 text-lg"
            >
              {hasGeneratedWrapped
                ? `Your ${currentYear} Wrapped is ready!`
                : `You have progress saved for ${currentYear}`}
            </motion.p>
          </div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-4 pt-4"
          >
            <Button onClick={handleContinue} className="w-full" size="lg">
              {hasGeneratedWrapped ? 'View Your Wrapped' : 'Continue Where I Left Off'}
            </Button>
            <Button variant="ghost" onClick={handleStartFresh} className="w-full text-white/70">
              Start Fresh Instead
            </Button>
          </motion.div>

          {/* Footer options */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="pt-4 space-y-2"
          >
            <button
              onClick={handleNotMe}
              className="w-full text-white/50 text-sm hover:text-white/80 transition-colors"
            >
              Not {savedName}? Switch user
            </button>
            <button
              onClick={handleResetAll}
              className="w-full text-white/30 text-xs hover:text-white/60 transition-colors"
            >
              Delete all my data
            </button>
          </motion.div>

          {/* Confirmation Dialog */}
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            title={
              confirmDialog.type === 'startFresh'
                ? 'Start Fresh?'
                : 'Delete All Data?'
            }
            message={
              confirmDialog.type === 'startFresh'
                ? `This will clear all your progress for ${currentYear}. You can't undo this.`
                : 'This will permanently delete ALL your data from all years. You can\'t undo this.'
            }
            confirmLabel={confirmDialog.type === 'startFresh' ? 'Start Fresh' : 'Delete Everything'}
            variant="danger"
            onConfirm={handleConfirmAction}
            onCancel={handleCancelDialog}
          />
        </motion.div>
      </div>
    )
  }

  // Returning user with past year data only
  if (savedName && hasPastYearData) {
    return (
      <div className="wrapped-card bg-gradient-to-br from-green to-teal">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8 max-w-md"
        >
          {/* Year badge */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring" }}
            className="mx-auto w-20 h-20 rounded-full bg-white/20 flex items-center justify-center"
          >
            <span className="text-3xl font-black">{currentYear}</span>
          </motion.div>

          {/* Welcome message */}
          <div className="space-y-2">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="wrapped-title"
            >
              Hey, {savedName}!
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-white/80 text-xl"
            >
              Ready to wrap up {currentYear}?
            </motion.p>
          </div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="space-y-4 pt-4"
          >
            <Button onClick={handleReview} disabled={isLoggingIn} className="w-full" size="lg">
              {isLoggingIn ? 'Loading...' : `Review ${lastYear} First`}
            </Button>
            <Button variant="secondary" onClick={handleStart} disabled={isLoggingIn} className="w-full">
              Start Fresh
            </Button>
            <button
              onClick={handleNotMe}
              className="w-full text-white/50 text-sm hover:text-white/80 transition-colors pt-2"
            >
              Not {savedName}? Start over
            </button>
            <button
              onClick={handleResetAll}
              className="w-full text-white/30 text-xs hover:text-white/60 transition-colors pt-4"
            >
              Reset all data
            </button>
          </motion.div>

          {/* Confirmation Dialog */}
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            title={
              confirmDialog.type === 'startFresh'
                ? 'Start Fresh?'
                : 'Delete All Data?'
            }
            message={
              confirmDialog.type === 'startFresh'
                ? `This will clear all your progress for ${currentYear}. You can't undo this.`
                : 'This will permanently delete ALL your data from all years. You can\'t undo this.'
            }
            confirmLabel={confirmDialog.type === 'startFresh' ? 'Start Fresh' : 'Delete Everything'}
            variant="danger"
            onConfirm={handleConfirmAction}
            onCancel={handleCancelDialog}
          />
        </motion.div>
      </div>
    )
  }

  return (
    <div className="wrapped-card bg-gradient-to-br from-pink to-orange">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="space-y-10 max-w-md w-full"
      >
        {/* Logo */}
        <div className="space-y-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="text-7xl"
          >
            ✨
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="wrapped-title"
          >
            Year Wrapped
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-white/90 text-2xl font-semibold"
          >
            Your {currentYear} in Review
          </motion.p>
        </div>

        {/* Name input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="space-y-4"
        >
          <p className="text-white/70 text-lg">What's your name?</p>
          <Input
            type="text"
            placeholder="Enter your name"
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            className="text-center"
            disabled={isLoggingIn}
          />
          {loginError && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-red-400 text-sm text-center"
            >
              {loginError}
            </motion.p>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            onClick={handleStart}
            disabled={!inputName.trim() || isLoggingIn}
            className="w-full"
            size="lg"
          >
            {isLoggingIn ? 'Getting ready...' : "Let's Go"}
          </Button>
        </motion.div>

        {/* Kids Mode Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <button
            onClick={handleKidsMode}
            className="w-full py-4 px-6 bg-gradient-to-r from-yellow/20 to-green/20 border-2 border-yellow/40 rounded-full text-white font-bold text-lg hover:from-yellow/30 hover:to-green/30 hover:border-yellow/60 transition-all flex items-center justify-center gap-3"
          >
            <span className="text-2xl">🧒</span>
            Kids Mode
            <span className="text-2xl">🌟</span>
          </button>
          <p className="text-white/50 text-xs text-center mt-2">
            Fun activities for ages 7-11
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="space-y-2"
        >
          <p className="text-white/50 text-sm">
            Takes about 10 minutes
          </p>
          <button
            onClick={handleResetAll}
            className="text-white/30 text-xs hover:text-white/60 transition-colors"
          >
            Reset all data
          </button>
        </motion.div>

        {/* Confirmation Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={
            confirmDialog.type === 'startFresh'
              ? 'Start Fresh?'
              : 'Delete All Data?'
          }
          message={
            confirmDialog.type === 'startFresh'
              ? `This will clear all your progress for ${currentYear}. You can't undo this.`
              : 'This will permanently delete ALL your data from all years. You can\'t undo this.'
          }
          confirmLabel={confirmDialog.type === 'startFresh' ? 'Start Fresh' : 'Delete Everything'}
          variant="danger"
          onConfirm={handleConfirmAction}
          onCancel={handleCancelDialog}
        />
      </motion.div>
    </div>
  )
}
